import { useCallback } from 'react';
import { Loader2 } from 'lucide-react';
import { PageTitle } from '@/components/page-title';
import { PageDescription } from '@/components/page-description';
import { useOrgStore } from '@/stores/org-store';
import { useGetOrgAdminCatalog } from '@/hooks/queries/nodes/use-get-org-admin-catalog';
import { useUpdateOrgConfig } from '@/hooks/mutations/nodes/use-update-org-config';
import { NodeCatalogList } from '@/components/node-catalog-list';

export const OrgNodesSettingsPage = () => {
  const { currentOrg } = useOrgStore();
  const orgId = currentOrg?.orgid;
  const { data: catalog = [], isLoading } = useGetOrgAdminCatalog(orgId);
  const { mutateAsync: saveConfig } = useUpdateOrgConfig();

  const getOriginalValue = useCallback(
    (nodeId: string): boolean => {
      const item = catalog.find((i) => i.node_id === nodeId);
      return item?.enabled_by_org ?? true;
    },
    [catalog],
  );

  const onSave = useCallback(
    async (changes: Record<string, boolean>) => {
      if (!orgId) return;
      await saveConfig({ orgId, items: changes });
    },
    [orgId, saveConfig],
  );

  return (
    <div className='w-full'>
      <div className='mb-6'>
        <PageTitle title='Nodes & Tools' />
        <PageDescription>
          Choose which nodes and tools are visible to users in your
          organization.
        </PageDescription>
      </div>

      {isLoading ? (
        <div className='flex items-center justify-center py-12'>
          <Loader2 className='size-6 animate-spin text-muted-foreground' />
          <span className='ml-2 text-muted-foreground'>
            Loading nodes and tools...
          </span>
        </div>
      ) : (
        <NodeCatalogList
          items={catalog}
          getOriginalValue={getOriginalValue}
          onSave={onSave}
          successMessage='Node configuration saved successfully'
        />
      )}
    </div>
  );
};
